import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { TimeObject, TimerContextValue, TimerProps } from '.';

const TimerContext = createContext<TimerContextValue | undefined>(undefined);

function useTimer() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('Timer components must be used within a TimerProvider');
  }
  return context;
}

const isDate = (value: Date | TimeObject): value is Date => {
  return value instanceof Date;
};

const formatTimeUnit = (unit: number) => unit.toString().padStart(2, '0');

export function Timer(timerProps: TimerProps) {
  const { time, onTimeChange, formatString = 'HH:mm:ss', autoUpdate = true, direction = 'up', interval = 1000, className, ...props } = timerProps;

  const [internalTime, setInternalTime] = useState<Date | TimeObject | undefined>(time);

  const updateTime = (newTime: Date) => {
    setInternalTime(newTime);
    onTimeChange?.(newTime);
  };

  useEffect(() => {
    if (!autoUpdate || !internalTime || !isDate(internalTime)) return;

    const timer = setInterval(() => {
      setInternalTime((prevTime) => {
        if (!prevTime || !isDate(prevTime)) return prevTime;
        const newTime: Date = direction === 'down' ? new Date(prevTime.getTime() - interval) : new Date(prevTime.getTime() + interval);
        onTimeChange?.(newTime);
        return newTime;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [autoUpdate, interval, direction, onTimeChange, internalTime]);

  useEffect(() => {
    if (time) setInternalTime(time);
  }, [time]);

  return (
    <TimerContext.Provider
      value={{
        time: internalTime,
        formatString,
        updateTime,
      }}
    >
      <div className={cn('flex gap-0.5', className)} {...props} />
    </TimerContext.Provider>
  );
}

export function TimerSeparator(timerSeparatorProps: ComponentProps<'span'>) {
  const { className, children = ':', ...props } = timerSeparatorProps;
  return (
    <span className={cn('text-muted-foreground text-sm', className)} {...props}>
      {children}
    </span>
  );
}
export function TimerBox(timerBoxProps: ComponentProps<'div'>) {
  const { className, ...props } = timerBoxProps;
  return <div className={cn('bg-muted text-muted-foreground flex h-6 w-7 shrink-0 items-center justify-center rounded-sm text-xs font-medium', className)} {...props} />;
}

export function TimerHours(timerHoursProps: ComponentProps<'div'>) {
  const { className, ...props } = timerHoursProps;
  const { time } = useTimer();

  const hours: string | null = useMemo(() => {
    if (!time) return null;
    return isDate(time) ? format(time, 'HH') : formatTimeUnit(time.hours);
  }, [time]);
  return (
    <TimerBox className={className} {...props}>
      {hours ?? '--'}
    </TimerBox>
  );
}

export function TimerMinutes(timerMinutesProps: ComponentProps<'div'>) {
  const { className, ...props } = timerMinutesProps;
  const { time } = useTimer();

  const minutes: string | null = useMemo(() => {
    if (!time) return null;
    return isDate(time) ? format(time, 'mm') : formatTimeUnit(time.minutes);
  }, [time]);

  return (
    <TimerBox className={className} {...props}>
      {minutes ?? '--'}
    </TimerBox>
  );
}

export function TimerSeconds(timerSecondsProps: ComponentProps<'div'>) {
  const { className, ...props } = timerSecondsProps;
  const { time } = useTimer();

  const seconds: string | null = useMemo(() => {
    if (!time) return null;
    return isDate(time) ? format(time, 'ss') : formatTimeUnit(time.seconds);
  }, [time]);
  return (
    <TimerBox className={className} {...props}>
      {seconds ?? '--'}
    </TimerBox>
  );
}
